import { useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogContentText, Typography, makeStyles } from '@material-ui/core';
import { ImageField, PhotoUploader } from 'client/components/PhotoUploader';
import { useGetProtectedObject } from 'common/hooks/storage/useGetProtectedObject';
import { useNotifier } from 'common/hooks/useNotifier';
import Message from 'common/models/Message';
import { useDoctorPhotoUploadStart } from 'mobile/hooks/doctor/useDoctorPhotoUploadStart';
import { usePhotoUpload } from 'mobile/hooks/procedure/usePhotoUpload';

const styles = {
    btnCancel: {
        border: '1px solid #2b333f',
        color: '#2b333f',
        '&:active': {
            backgroundColor: '1px solid #2b333f',
            boxShadow: 'none',
        },
        '&:hover': {
            backgroundColor: '1px solid #2b333f',
            boxShadow: 'none',
        },
        '&:focus': {
            backgroundColor: '1px solid #2b333f',
            boxShadow: 'none',
        },
        '&:disabled': {
            backgroundColor: '#eee !important',
            color: '#ccc !important',
        },
    },
    btnCOnfirm: {
        marginLeft: '5px',
    },
    boxDoctorAvatar: {
        width: '200px',
        height: '200px',
        background: '#bbb',
        borderRadius: '50%',
        border: '1px solid #bbb',
        display: 'block',
    },
};

const useStyles = makeStyles(styles);

interface Props {
    open: boolean;
    doctorId: string;
    name?: string;
    photoUrl?: string;
    close: () => void;
    update: (photoUrl?: string) => void;
}

export const DoctorProfileImageDialog = ({ open, doctorId, name, photoUrl, close, update }: Props) => {
    const classes = useStyles();
    const [isProgress, setProgress] = useState(false);
    const [showUploader, setShowUploader] = useState(false);
    const { notify } = useNotifier();
    const useDoctorPhotoUploadStartMutate = useDoctorPhotoUploadStart();
    const photoUpload = usePhotoUpload();
    const [computedPhotoUrl, setComputedPhotoUrl] = useState(photoUrl);
    const doctorPhoto = useGetProtectedObject(computedPhotoUrl);

    const handleUpload = async (imageField: ImageField, image: Blob) => {
        setProgress(true);

        try {
            const result = await useDoctorPhotoUploadStartMutate.useDoctorPhotoUploadStart({
                doctorId: Number(doctorId),
            });

            await photoUpload.mutateAsync({
                uploadUrl: result.uploadUrl,
                blob: image,
                filename: `doctor-${doctorId}-profile.png`,
            });

            setComputedPhotoUrl(result.photoUrl);
            update(result.photoUrl);
        } catch (error) {
            console.error('Error uploading image:', error);
            notify(new Message({ title: 'Error uploading image', type: 'error' }));
        } finally {
            setProgress(false);
            setShowUploader(false);
        }
    };

    const handleNotifier = (message: Message) => {
        notify(message);
    };

    return (
        <>
            <Dialog open={open} aria-labelledby="form-dialog-title">
                <DialogContent>
                    <DialogContentText color="primary">
                        <Box p={2} style={{ width: '25vw' }}>
                            <Typography variant="h6">{name} Profile Image</Typography>
                            <Box p={1}></Box>
                            <img src={doctorPhoto} alt={name} className={classes.boxDoctorAvatar} />
                            <Box p={1}></Box>
                            <Box sx={{ display: 'flex', flexDirection: 'row', p: 1, m: 1, justifyContent: 'flex-start' }}>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    color="secondary"
                                    onClick={close}
                                    disabled={isProgress}
                                    className={classes.btnCancel}
                                >
                                    Close
                                </Button>
                                <Button
                                    autoFocus
                                    variant="contained"
                                    size="medium"
                                    onClick={() => setShowUploader(true)}
                                    disabled={isProgress}
                                    className={classes.btnCOnfirm}
                                >
                                    Upload New Image
                                    <Box sx={{ ml: 1, mr: 1 }}>{isProgress ? <CircularProgress size={'0.8rem'} color="inherit" /> : <></>}</Box>
                                </Button>
                            </Box>
                            <Box p={2}></Box>
                        </Box>
                    </DialogContentText>
                </DialogContent>
            </Dialog>
            {showUploader && (
                <PhotoUploader
                    imageField={ImageField.picture}
                    onUpload={handleUpload}
                    onNotifier={handleNotifier}
                    onClose={() => setShowUploader(false)}
                />
            )}
        </>
    );
};
